import '.as{color:var(--primary-blue);}.ssr-plugin'
import '.at{font-family:FunnelDisplay;}.ssr-plugin'
import '.au{font-weight:400;}.ssr-plugin'
import '.av{font-style:normal;}.ssr-plugin'
import '.aw{font-size:1.5rem;}.ssr-plugin'
import '.ax{line-height:140%;}.ssr-plugin'
import useMediaQuery from '@app/util/useMediaQuery'
import { styled, type Style } from 'inlines'
import type { ReactNode } from 'react'
import { match } from 'ts-pattern'

export type TextProps = {
  children: ReactNode
  style?: Style
}

const sharedTitleStyles: Style = {
  color: 'var(--primary-blue)',
  fontFamily: 'FunnelDisplay',
  fontWeight: '700',
  fontStyle: 'normal',
  marginTop: 0,
}

const H1 = ({ children, style }: TextProps) => {
  const isMobile = useMediaQuery()

  return (
    <h1
      style={{
        ...sharedTitleStyles,
        fontSize: isMobile ? '3rem' : '5.625rem',
        lineHeight: isMobile ? '130%' : '100%',
        marginBottom: '1.5rem',
        ...style,
      }}
    >
      {children}
    </h1>
  )
}

const H2 = ({ children, style }: TextProps) => {
  const isMobile = useMediaQuery()

  return (
    <h2
      style={{
        ...sharedTitleStyles,
        fontSize: isMobile ? '2rem' : '3rem',
        lineHeight: isMobile ? '130%' : '100%',
        marginBottom: '1.5rem',
        ...style,
      }}
    >
      {children}
    </h2>
  )
}

const H3 = ({ children, style }: TextProps) => {
  const isMobile = useMediaQuery()

  return (
    <h3
      style={{
        ...sharedTitleStyles,
        fontSize: isMobile ? '1.75rem' : '2rem',
        lineHeight: isMobile ? '110%' : '130%',
        letterSpacing: '-0.02rem',
        marginBottom: '1rem',
        ...style,
      }}
    >
      {children}
    </h3>
  )
}

const H4 = ({ children, style }: TextProps) => {
  const isMobile = useMediaQuery()

  return (
    <h4
      style={{
        ...sharedTitleStyles,
        fontSize: isMobile ? '1.375rem' : '1.75rem',
        lineHeight: isMobile ? '110%' : '100%',
        marginBottom: '1rem',
        ...style,
      }}
    >
      {children}
    </h4>
  )
}

const H5 = ({ children, style }: TextProps) => {
  const isMobile = useMediaQuery()

  return (
    <h5
      style={{
        ...sharedTitleStyles,
        fontSize: '1.125rem',
        lineHeight: isMobile ? '110%' : '120%',
        letterSpacing: '-0.01125rem',
        marginBottom: '1rem',
        ...style,
      }}
    >
      {children}
    </h5>
  )
}

const TitleRoot = ({ children }: { children: React.ReactNode }) => {
  return children
}

const TitlePropsBased = (
  props: TextProps & {
    title: 'H1' | 'H2' | 'H3' | 'H4' | 'H5'
  },
) => {
  return match(props.title)
    .with('H1', () => H1(props))
    .with('H2', () => H2(props))
    .with('H3', () => H3(props))
    .with('H4', () => H4(props))
    .with('H5', () => H5(props))
    .exhaustive()
}

TitleRoot.H1 = H1
TitleRoot.H2 = H2
TitleRoot.H3 = H3
TitleRoot.H4 = H4
TitleRoot.H5 = H5
TitleRoot.Hx = TitlePropsBased

export const Title = TitleRoot

export const Subtitle = ({ children, style }: TextProps) => {
  return (
    <styled.div className="as at au av aw ax"
      role="doc-subtitle"
      style={{
        ...style,
      }}
    >
      {children}
    </styled.div>
  )
}
